import * as Dialog from '@radix-ui/react-dialog';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch } from '~/store';

import { Tooltip } from '@bufferapp/ui';

import { openUploadcareModal } from '~/uploadcare/openUploadcareModal';
import {
  DeleteIcon,
  EditIcon,
  PlayIcon,
} from '~/content/components/Editor/styles';

import { addPermanentNotification } from '~/notifications-provider/notificationsSlice';
import {
  UploadcareIntegrations,
  UploadcareSources,
} from '~/uploadcare/constants';
import { useIsIntegrationUploading } from '~/integrations-bar/state/useIsIntegrationUploading';
import { removeUpload } from '../state/thunks/removeUpload';
import { Upload } from '../entities/Upload';
import { MediaPreviewer } from './MediaPreviewer';
import { CanvaEditTag } from './CanvaEditTag';
import { useUploader } from '../hooks/useUploader';

const ThumbnailBox = styled.div`
  position: relative;
  width: 103px;
  height: 103px;
  cursor: pointer;
  > img {
    border-radius: 4px;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// TODO: before enabling this we need to validate:
// - that it's OK to keep the image in the uploads (we could replace it instead)
// - review the crop presets provied for ideas in getUploadCareEditOptions
// - that 'ideas' is allowed in the tracking plan (and update openUploadcareModal)
const IS_EDIT_ENABLED = false;

export const Thumbnail = ({
  upload,
  containerRef,
}: {
  upload: Upload;
  // There was a problem with passing 'null' here as default
  // eslint-disable-next-line react/require-default-props
  containerRef?: HTMLElement | null | undefined;
}) => {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const { onFileReady } = useUploader({
    id: upload.uploaderId,
    onUnmount: 'keep',
  });
  const { onUploadStarted, onUploadFinished } = useIsIntegrationUploading();

  const onError = ({ message }) => {
    dispatch(addPermanentNotification(message));
  };

  const onThumbnailClick = () => {
    setIsOpen(true);
  };

  const onOpenChange = (open: boolean) => {
    if (open) setIsOpen(true);
    else setIsOpen(false);
  };

  const onDeleteClick = () => {
    dispatch(removeUpload(upload));
  };

  const onEditClick = () => {
    openUploadcareModal(
      'ideas',
      onFileReady,
      onError,
      UploadcareIntegrations.EDIT,
      onUploadStarted,
      onUploadFinished,
      UploadcareSources.EDIT,
      {
        url: upload.url,
      },
      undefined,
      'ideasEditor'
    );
  };

  const tooltipText = Upload.canAddDescription(upload)
    ? 'Expand and Add Description'
    : 'Expand';

  return (
    <div data-testid="media-manager-thumbnail">
      <Tooltip label={tooltipText} position="top">
        <ThumbnailBox>
          <DeleteIcon onClick={onDeleteClick} />
          <img
            src={Upload.getThumbnailUrl(upload)}
            alt={upload.alt || upload.name}
            onClick={onThumbnailClick}
            onKeyPress={onThumbnailClick}
            role="presentation"
          />
          <CanvaEditTag upload={upload} />
          {IS_EDIT_ENABLED && Upload.isImage(upload) && (
            <EditIcon onClick={onEditClick} />
          )}
          {Upload.isVideo(upload) && <PlayIcon />}
        </ThumbnailBox>
      </Tooltip>
      <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
        <Dialog.Portal container={containerRef}>
          <MediaPreviewer upload={upload} onClose={() => setIsOpen(false)} />
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

Thumbnail.propTypes = {};

Thumbnail.defaultProps = {};
